import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import t from 'prop-types';
import maskSalary from 'maskSalary';
import getCountryName from 'getCountryName';
import { useAppContext } from 'appContext';

function SalaryComparison({ searchParams, salary, intl }) {
  const [annualSalary, setAnnualSalary] = useState(0);
  const { currencySettings: { currencySign, salarySeparator } } = useAppContext();

  /**
   * Set annual salary through the state,
   * to prevent render difference between Server and Client sides.
   */
  useEffect(() => {
    if (typeof searchParams.salary !== 'undefined') {
      setAnnualSalary(parseFloat(searchParams.salary));
    }
  }, [searchParams.salary]);

  /**
   * Compare two salaries.
   * Using IIFE to prevent function calls in further code, use result value instead.
   * @returns {string}
   *   One of the following: More, Less, Equal.
   *   We will use the same strings in the i18n translations.
   */
  const salaryState = (() => {
    let state;
    // equal, if difference is less than 0.5%
    if (
      annualSalary === salary
      || (Math.abs((annualSalary - salary)) < (salary * 0.005))
    ) {
      state = 'Equal';
    } else {
      state = annualSalary > salary ? 'More' : 'Less';
    }
    return state;
  })();

  // Store aria-label string in separate variable, to avoid eslint error with nested template strings.
  const arrowAriaLabel = (state) => intl.formatMessage({ id: `Icon.Arrow.${state}.AriaLabel` });

  /**
   * String with the HTML parts with arrow-icon and calculated salary difference in percentages,
   * or simple string if compared salaries are the same,
   * depending on input data.
   *
   * @return {String}
   */
  const formattedValue = () => (salaryState === 'Equal'
    ? intl.formatMessage({ id: `Salary.PerYear.${salaryState}` })
    : intl.formatMessage(
      { id: `Salary.PerYear.${salaryState}` },
      {
        value: Math.abs((100 * (1 - annualSalary / salary)).toFixed(1)),
        arrow: `<svg width="14" height="18" viewBox="0 0 14 18" aria-label="${arrowAriaLabel(salaryState)}">
          <path d="M7.93728,17.00001V3.39601l4.35915,4.315c.39301,.389,1.02604,.385,1.41405-.008,.38901-.391,.38501-1.026-.006-1.414L7.64127,.28901C7.45326,.10301,7.19825-.00099,6.93224,0c-.26601,.001-.51902,.108-.70602,.297L.28901,6.29701c-.19301,.194-.28901,.449-.28901,.703,0,.258,.099,.516,.29601,.711,.39301,.389,1.02604,.385,1.41405-.008L5.93721,3.43201v13.568c0,.553,.44802,1,1.00003,1,.55302,0,1.00003-.447,1.00003-1"/>
        </svg>`,
      },
    ));

  /**
   * Color of the message depends on salary comparison result.
   * One of: equal, more, less.
   * @param {String} getSalaryStateClass
   * @return {string}
   */
  const formatsName = (getSalaryStateClass) => {
    const currentState = getSalaryStateClass.toLocaleLowerCase();
    return currentState === 'equal' ? `${currentState}` : `${currentState} text--${currentState === 'less' ? 'negative' : 'positive'}`;
  };

  /**
   * Returns a complex string with translation and parts of HTML code.
   *
   * @return {String}
   */
  const translatedSalaryInfo = intl.formatMessage(
    { id: 'Salary.PerYear.Info' },
    {
      value: `<span class="${formatsName(salaryState)}"> ${formattedValue()} </span>`,
      state: salaryState.toLowerCase(),
      location: searchParams.city && searchParams.region ? `${searchParams.city}, ${searchParams.region}` : getCountryName(intl.locale),
    },
  );

  return (
    <div className="salary-block salary-block__annual" data-testid="salary-comparison">
      <div className={`${annualSalary === 0 ? 'content-loading' : ''} loader-container`}>
        <div className="loader__wrapper">
          <span className="loader" />
        </div>
      </div>
      <div className={`salary-block__icon icon icon--inline icon__salary fill-brand--${process.env.REACT_APP_COUNTRY === 'sph' ? 'orange' : 'blue'}`}>
        <svg aria-label={intl.formatMessage({ id: 'Icon.Badge.AriaLabel' })}>
          <use
            xlinkHref="/themes/custom/bluex/dist/assets/image/icons.svg#badge"
          />
        </svg>
      </div>
      {annualSalary
        ? (
          <div>
            <h2 className="title--s">
              {intl.formatMessage({ id: 'Salary.Annual.Title' })}
            </h2>
            <div className="salary-block__salary_info">
              <span className={`salary-block__salary_num text-brand-primary ${process.env.REACT_APP_COUNTRY === 'sph' ? 'title--m-slim' : 'title--l'}`}>
                {currencySign}
                {maskSalary(annualSalary, salarySeparator)}
              </span>
              {intl.formatMessage({ id: 'Salary.PerYear.Text' })}
            </div>
            <div className="salary-block__annual__comparison_info">
              {/* Complex component */}
              {/* eslint-disable-next-line */}
              <p dangerouslySetInnerHTML={{ __html: translatedSalaryInfo }} />
            </div>
          </div>
        )
        : (
          <div>
            <h2 className="title--s">
              {intl.formatMessage({ id: 'Salary.Annual.LearnMore.Title' })}
            </h2>
            <p>
              {intl.formatMessage({ id: 'Salary.Annual.LearnMore.Description' })}
            </p>
          </div>
        )}
    </div>
  );
}

SalaryComparison.propTypes = {
  searchParams: t.shape({
    occupation: t.string,
    region: t.string,
    city: t.string,
    salary: t.string,
  }).isRequired,
  salary: t.number.isRequired,
};

export default injectIntl(SalaryComparison);
